import { System } from '@ecs/System';
import { Entity } from '@ecs/Entity';
import { Position } from '@components/Position';
import { Velocity } from '@components/Velocity';
import { Renderable } from '@components/Renderable';
import { Lifetime } from '@components/Lifetime';
import { Tag } from '@components/Tag';
import { ChargeEffectRequest } from '@components/ChargeEffectRequest';
import { VisualEffect } from '@components/VisualEffect';

export class EffectRequestSystem extends System {
  update(_deltaTime: number): void {
    // Process charge effect requests
    const chargeRequests = this.getEntitiesWithComponents(ChargeEffectRequest);
    for (const entity of chargeRequests) {
      const request = entity.getComponent(ChargeEffectRequest)!;
      this.createChargeEffect(request);
      
      // Remove the request component after processing
      entity.removeComponent(ChargeEffectRequest);
    }

    // Process visual effects
    const visualEffects = this.getEntitiesWithComponents(VisualEffect, Position);
    for (const entity of visualEffects) {
      const effect = entity.getComponent(VisualEffect)!;
      const position = entity.getComponent(Position)!;
      
      switch (effect.effectType) {
        case 'muzzleFlash':
          this.createMuzzleFlash(position.x, position.y, effect.params);
          break;
        case 'torpedoLaunch':
          this.createTorpedoLaunchEffect(position.x, position.y, effect.params);
          break;
      }
      
      // Remove the effect component after processing
      entity.removeComponent(VisualEffect);
    }
  }

  private createChargeEffect(request: ChargeEffectRequest): void {
    const currentTime = Date.now();
    
    // Create particles based on charge progress
    for (let i = 0; i < request.particleCount; i++) {
      const angle = (Math.PI * 2 * i) / request.particleCount + currentTime * 0.002;
      const distance = 20 + request.chargeProgress * 15; // Distance grows with charge
      const particleX = request.x + Math.cos(angle) * distance;
      const particleY = request.y + Math.sin(angle) * distance;
      
      // Color intensity increases with charge
      const colorIntensity = Math.floor(100 + request.chargeProgress * 155);
      const color = `rgb(255, 0, ${colorIntensity})`;
      
      const particle = new Entity()
        .addComponent(new Position(particleX, particleY))
        .addComponent(new Velocity(
          angle + Math.PI, // Move inward
          request.chargeProgress * 2
        ))
        .addComponent(new Renderable(
          'particle',
          color,
          2 + request.chargeProgress * 2, // Size increases with charge
          undefined,
          2,
          color,
          5 + request.chargeProgress * 10 // Glow increases with charge
        ))
        .addComponent(new Lifetime(10, 10))
        .addComponent(new Tag('particle'));

      this.world.addEntity(particle);
    }
    
    // Add lightning-like lines at high charge
    if (request.chargeProgress > 0.5) {
      const lineCount = Math.floor(request.chargeProgress * 8);
      for (let i = 0; i < lineCount; i++) {
        const angle = (Math.PI * 2 * i) / lineCount + currentTime * 0.003;
        const lineLength = 15 + Math.random() * 20 * request.chargeProgress;
        
        const particle = new Entity()
          .addComponent(new Position(request.x, request.y))
          .addComponent(new Velocity(angle, lineLength / 5))
          .addComponent(new Renderable(
            'particle',
            `rgba(255, 100, 255, ${0.4 + Math.random() * 0.4})`,
            1,
            undefined,
            1
          ))
          .addComponent(new Lifetime(5, 5))
          .addComponent(new Tag('particle'));

        this.world.addEntity(particle);
      }
    }
  }

  private createMuzzleFlash(x: number, y: number, params: any): void {
    const angle = params.angle || 0;
    const color = params.color || '#00ffff';
    const size = params.size || 10;
    
    // Create flash particles
    for (let i = 0; i < 8; i++) {
      const spread = (Math.random() - 0.5) * 0.5;
      const particleAngle = angle + spread;
      const speed = 2 + Math.random() * 3;
      
      const particle = new Entity()
        .addComponent(new Position(x, y))
        .addComponent(new Velocity(particleAngle, speed))
        .addComponent(new Renderable(
          'particle',
          color,
          size * (0.5 + Math.random() * 0.5),
          undefined,
          2,
          color,
          10
        ))
        .addComponent(new Lifetime(10, 10))
        .addComponent(new Tag('particle'));

      this.world.addEntity(particle);
    }
  }

  private createTorpedoLaunchEffect(x: number, y: number, params: any): void {
    const launchAngle = params.launchAngle || 0;
    
    // Create smoke puff
    for (let i = 0; i < 12; i++) {
      const spread = (Math.random() - 0.5) * Math.PI / 2;
      const particleAngle = launchAngle + spread;
      const speed = 1 + Math.random() * 2;
      
      const particle = new Entity()
        .addComponent(new Position(x, y))
        .addComponent(new Velocity(particleAngle, speed))
        .addComponent(new Renderable(
          'particle',
          `rgba(150, 150, 150, ${0.5 + Math.random() * 0.3})`,
          3 + Math.random() * 2
        ))
        .addComponent(new Lifetime(30, 30))
        .addComponent(new Tag('particle'));

      this.world.addEntity(particle);
    }
    
    // Create launch flash
    const flash = new Entity()
      .addComponent(new Position(x, y))
      .addComponent(new Velocity(launchAngle, 0))
      .addComponent(new Renderable(
        'particle',
        '#ff8800',
        8,
        undefined,
        2,
        '#ff8800',
        15
      ))
      .addComponent(new Lifetime(5, 5))
      .addComponent(new Tag('particle'));

    this.world.addEntity(flash);
  }
}